import { BiometricAuth, BiometryType, getBiometryName } from '@aparajita/capacitor-biometric-auth';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { Capacitor } from '@capacitor/core';

import {
  canUseWebBiometric,
  disableWebBiometricLogin,
  enableWebBiometricLogin,
  isWebBiometricEnabled,
  refreshStoredWebToken,
  webBiometricUnlock,
} from './webBiometric';

// Key for the Keychain (iOS) / encrypted Keystore (Android) entry. Never localStorage.
const TOKEN_KEY = 'auth.refreshToken';
// Flag only — marks that the user opted in. Safe to keep in localStorage.
const BIO_FLAG = 'BIOMETRIC_ENABLED';

const isNative = (): boolean => Capacitor.isNativePlatform();

export const isBiometricEnabled = (): boolean =>
  isNative() ? window.localStorage.getItem(BIO_FLAG) === 'true' : isWebBiometricEnabled();

/** Native: hardware present AND a face/finger enrolled. Web: platform authenticator + PRF-capable browser. */
export const canUseBiometric = async (): Promise<boolean> => {
  if (!isNative()) return canUseWebBiometric();
  try {
    const { isAvailable } = await BiometricAuth.checkBiometry();
    return isAvailable;
  } catch {
    return false;
  }
};

/** Coarse category for picking an icon, independent of the exact platform biometry. */
export type BiometryKind = 'face' | 'fingerprint' | 'iris' | 'biometrics';

const kindForType = (type: BiometryType): BiometryKind => {
  switch (type) {
    case BiometryType.faceId:
    case BiometryType.faceAuthentication:
      return 'face';
    case BiometryType.touchId:
    case BiometryType.fingerprintAuthentication:
      return 'fingerprint';
    case BiometryType.irisAuthentication:
      return 'iris';
    default:
      return 'biometrics';
  }
};

/** Label + icon-kind for the button, e.g. { label: "Face ID", kind: "face" }. */
export const getBiometryInfo = async (): Promise<{ label: string; kind: BiometryKind }> => {
  // On web the browser never tells us the underlying modality (Touch ID vs Hello vs
  // fingerprint), so use a neutral label + icon.
  if (!isNative()) return { label: 'Passkey', kind: 'biometrics' };
  try {
    const { biometryType } = await BiometricAuth.checkBiometry();
    return {
      label: getBiometryName(biometryType) || 'Biometrics',
      kind: kindForType(biometryType),
    };
  } catch {
    return { label: 'Biometrics', kind: 'biometrics' };
  }
};

/** Human label for the button, e.g. "Face ID" / "Touch ID" / "Fingerprint". */
export const getBiometryLabel = async (): Promise<string> => (await getBiometryInfo()).label;

/**
 * Call after a successful login once the user opts in. Stores the refresh token securely.
 * `userName` (web only) labels the created passkey in the browser's credential UI.
 */
export const enableBiometricLogin = async (
  refreshToken: string,
  userName?: string,
): Promise<void> => {
  if (!isNative()) {
    await enableWebBiometricLogin(refreshToken, userName);
    return;
  }
  await SecureStorage.set(TOKEN_KEY, refreshToken);
  window.localStorage.setItem(BIO_FLAG, 'true');
};

/**
 * Silently replace the stored refresh token after the backend rotates it.
 * No-op unless biometric login is already enabled. Never throws — rotation must not break the request flow.
 */
export const refreshStoredToken = async (refreshToken: string): Promise<void> => {
  if (!isNative()) {
    await refreshStoredWebToken(refreshToken);
    return;
  }
  if (!refreshToken || !isBiometricEnabled()) return;
  try {
    await SecureStorage.set(TOKEN_KEY, refreshToken);
  } catch {
    // Retry once. A stale token left here is exactly what causes the
    // {"domain":"JWT","type":"NOT_FOUND"} biometric-unlock failure, so don't swallow it silently.
    try {
      await SecureStorage.set(TOKEN_KEY, refreshToken);
    } catch (retryErr) {
      // Give up after one retry — next unlock will fail and auto-disable biometric,
      // falling back to password. Acceptable degradation, but log loud so it's not invisible.
      console.error('Biometric keychain sync failed, stored token is stale:', retryErr);
    }
  }
};

/** Prompt Face ID / Touch ID, then read the stored refresh token. Returns null on cancel/fail/empty. */
export const biometricUnlock = async (): Promise<string | null> => {
  if (!isNative()) return webBiometricUnlock();
  try {
    await BiometricAuth.authenticate({
      reason: 'Sign in to Vessel Haven',
      cancelTitle: 'Cancel',
      iosFallbackTitle: 'Use Passcode',
      androidTitle: 'Sign In',
      androidSubtitle: 'Please confirm your identity.',
      allowDeviceCredential: true,
    });
    const token = await SecureStorage.get(TOKEN_KEY);
    return typeof token === 'string' && token ? token : null;
  } catch {
    // user cancelled, failed, or no stored credentials
    return null;
  }
};

/** Wipe the stored token and clear the opt-in flag. Call on logout and on "disable" toggle. */
export const disableBiometricLogin = async (): Promise<void> => {
  if (!isNative()) {
    await disableWebBiometricLogin();
    return;
  }
  window.localStorage.removeItem(BIO_FLAG);
  try {
    await SecureStorage.remove(TOKEN_KEY);
  } catch {
    // nothing stored — ignore
  }
};
